import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SEEN_KEY = "wba_splash_seen";

export default function SplashLoader() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(SEEN_KEY) !== "1";
  });

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => {
      sessionStorage.setItem(SEEN_KEY, "1");
      setVisible(false);
    }, 1800);
    return () => clearTimeout(t);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="splash"
          data-testid="splash-loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ background: "#050810" }}
        >
          {/* Hex grid backdrop */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(circle at 50% 50%, rgba(0,240,255,0.08), transparent 60%)",
            }}
          />

          {/* Concentric rings pulse */}
          <div className="pointer-events-none absolute h-[420px] w-[420px]">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="absolute inset-0 rounded-full border border-cyan/30"
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1.4, opacity: [0, 0.6, 0] }}
                transition={{
                  duration: 2.4,
                  delay: i * 0.5,
                  repeat: Infinity,
                  ease: "easeOut",
                }}
              />
            ))}
          </div>

          {/* Logo with subtle scale + glow */}
          <motion.div
            initial={{ scale: 0.85, opacity: 0, filter: "blur(6px)" }}
            animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 flex flex-col items-center"
          >
            <motion.img
              src="/logos/logo_navbar.png"
              alt="Wall Board Academy"
              className="h-40 w-40 object-contain md:h-52 md:w-52"
              animate={{
                filter: [
                  "drop-shadow(0 0 0px rgba(0,240,255,0))",
                  "drop-shadow(0 0 24px rgba(0,240,255,0.55))",
                  "drop-shadow(0 0 0px rgba(0,240,255,0))",
                ],
              }}
              transition={{ duration: 1.8, ease: "easeInOut", repeat: Infinity }}
            />
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="mt-6 text-center"
            >
              <p className="font-head text-lg font-extrabold uppercase tracking-[0.35em] text-white">
                Wall Board <span className="text-cyan">Academy</span>
              </p>
              <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.4em] text-slate-500">
                Advanced Abdominal Wall Surgery
              </p>
            </motion.div>

            {/* Loading bar */}
            <motion.div className="mt-8 h-[2px] w-40 overflow-hidden bg-white/5">
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: "100%" }}
                transition={{ duration: 1.6, ease: "easeInOut" }}
                className="h-full w-1/2 bg-cyan"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
